import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { forwardRef } from 'react';
import { cn } from 'src/domains/common/components/utils';

/* Menu components */
export const MenuWrapper = observer(({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn('rounded-md shadow bg-white border border-border border-solid', className)}
        {...props}
    >
        {children}
    </div>
));

interface MenuTitlePropsType extends React.HTMLAttributes<HTMLHeadingElement> {
    label: string;
}

export const MenuTitle = observer(({ label, className, ...props }: MenuTitlePropsType) => (
    <h3
        className={cn('p-2 m-0 font-semibold text-[1rem] leading-7', className)}
        {...props}
    >
        {label}
    </h3>
));

export const MenuSeparator = observer(({ className, ...props }: React.HTMLAttributes<HTMLHRElement>) => (
    <hr
        className={cn('h-px border-none bg-border mt-0.5 mb-0.5', className)}
        {...props}
    />
));

interface MenuItemWrapperPropsType extends React.HTMLAttributes<HTMLDivElement> {
    label: string;
    showChevron?: boolean;
}

export const MenuItemWrapper = observer(
    ({ label, showChevron = true, className, children, ...props }: MenuItemWrapperPropsType) => (
        <div
            className={cn(
                'cursor-pointer flex items-center outline-none px-2 py-1.5 relative rounded-sm select-none text-sm group',
                className
            )}
            {...props}
        >
            {label}
            {showChevron && <ChevronRightIcon className='w-4 h-4 ml-auto' />}
            {children}
        </div>
    )
);

/* Submenu components */
export const SubMenuWrapper = observer(
    forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ children, className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn('rounded-md shadow bg-white border border-border border-solid', className)}
            {...props}
        >
            {children}
        </div>
    ))
);

interface SubMenuItemWrapperPropsType extends React.HTMLAttributes<HTMLDivElement> {
    label: string;
    showCheckIcon?: boolean;
    isSelected?: boolean;
}

export const SubMenuItemWrapper = observer(
    ({ label, showCheckIcon = true, isSelected = false, className, ...props }: SubMenuItemWrapperPropsType) => (
        <div
            className={cn('flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer text-nowrap', className)}
            {...props}
        >
            {showCheckIcon && (
                <CheckIcon
                    className={cn('size-4 transition-opacity duration-200', isSelected ? 'opacity-100' : 'opacity-0')}
                />
            )}
            {label}
        </div>
    )
);
